import { fabric } from 'fabric';
import { arrowGeometry } from './drawing';

/*
 * หัวลูกศร on a line (5ก): a plain Fabric line with `arrowStart` / `arrowEnd` (EXTRA_PROPS), drawn here for every
 * canvas — the editor, pictures, previews and video all go through fabric.Line — with the geometry the print PDF
 * also uses (src/editor/drawing.ts arrowGeometry, src/print/pdf.ts). The heads are drawn in the line's on-page size,
 * so stretching the line never squashes them.
 * Such a line is not drawn through Fabric's object cache: the cache is the line's own box, which a horizontal line's
 * heads stick out of (they would be cut off).
 */

type ArrowLine = fabric.Line & { arrowStart?: boolean; arrowEnd?: boolean; ownCaching?: boolean };
type Proto = { _render(ctx: CanvasRenderingContext2D): void; shouldCache(): boolean };

const proto = fabric.Line.prototype as unknown as Proto & { __arrows?: boolean };
if (!proto.__arrows) {
  proto.__arrows = true;
  const plainRender = proto._render, plainCache = proto.shouldCache;

  proto.shouldCache = function (this: ArrowLine) {
    if (this.arrowStart || this.arrowEnd) { this.ownCaching = false; return false; }
    return plainCache.call(this);
  };

  // a head can stick out of the line's box: drawing in tiles (PDF ภาพ) must not skip the line at a tile's edge
  const plainOnScreen = (fabric.Line.prototype as unknown as { isOnScreen(c?: boolean): boolean }).isOnScreen;
  (fabric.Line.prototype as unknown as { isOnScreen(c?: boolean): boolean }).isOnScreen = function (this: ArrowLine, calculate?: boolean) {
    return this.arrowStart || this.arrowEnd ? true : plainOnScreen.call(this, calculate);
  };

  proto._render = function (this: ArrowLine, ctx: CanvasRenderingContext2D) {
    if (!(this.arrowStart || this.arrowEnd) || !this.stroke) { plainRender.call(this, ctx); return; }
    const sx = this.scaleX || 1, sy = this.scaleY || 1;
    const p = (this as unknown as { calcLinePoints(): { x1: number; y1: number; x2: number; y2: number } }).calcLinePoints();
    const sw = (this.strokeWidth ?? 1) * (this.strokeUniform ? 1 : Math.sqrt(Math.abs(sx * sy)));
    const g = arrowGeometry({ x: p.x1 * sx, y: p.y1 * sy }, { x: p.x2 * sx, y: p.y2 * sy }, sw, !!this.arrowStart, !!this.arrowEnd, this.strokeLineCap, !!this.strokeDashArray?.length);
    ctx.save();
    ctx.scale(1 / sx, 1 / sy);                         // on-page units from here
    ctx.strokeStyle = this.stroke as string;
    ctx.lineWidth = sw;
    ctx.lineCap = g.cap as CanvasLineCap;
    ctx.setLineDash(this.strokeDashArray ?? []);
    ctx.beginPath(); ctx.moveTo(g.a.x, g.a.y); ctx.lineTo(g.b.x, g.b.y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = this.stroke as string;
    for (const h of g.heads) {
      ctx.beginPath(); ctx.moveTo(h[0].x, h[0].y); for (const q of h.slice(1)) ctx.lineTo(q.x, q.y); ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  };
}

/** how far a line's heads reach past its own box (page px), for anything cut to that box (ดาวน์โหลดที่เลือก) */
export function arrowOverhang(o: fabric.Object): number {
  const a = o as ArrowLine;
  if (o.type !== 'line' || !(a.arrowStart || a.arrowEnd)) return 0;
  return Math.max(9, (o.strokeWidth ?? 1) * 3) / 2;
}
